import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, List, ListItemButton, ListItemText, Button, Divider, useTheme, useMediaQuery,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HelpMarkdown from "../components/HelpMarkdown";
import { tokens } from "../theme";
import "../markdown.css";

/**
 * The manual as a full page, so chapters are linkable and shareable
 * (/help and /help/:slug). Same generated content as the help drawer —
 * built from docs/manual/src by docs/manual/build.js.
 */

const HELP_BASE = (process.env.PUBLIC_URL || "") + "/help";

const Help = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug } = useParams();
  const navigate = useNavigate();

  const [index, setIndex] = useState(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${HELP_BASE}/index.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("index"))))
      .then(setIndex)
      .catch(() => setError("Could not load the manual."));
  }, []);

  const active = useMemo(
    () => (index ? index.chapters.find((c) => c.slug === slug) || index.chapters[0] : null),
    [index, slug]
  );

  useEffect(() => {
    if (!active) return;
    setBody("");
    fetch(`${HELP_BASE}/${active.slug}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(active.slug))))
      .then(setBody)
      .catch(() => setError("Could not load that chapter."));
    window.scrollTo(0, 0);
  }, [active]);

  if (error) return <Box m="20px"><Typography color="error">{error}</Typography></Box>;
  if (!index) return <Box m="20px"><Typography color={colors.grey[300]}>Loading…</Typography></Box>;

  return (
    <Box m={isMobile ? "10px" : "20px"} display="flex" justifyContent="center">
      <Box maxWidth="1200px" width="100%" display="flex" gap={3} flexDirection={isMobile ? "column" : "row"}>
        {/* contents */}
        <Box
          sx={{
            width: isMobile ? "100%" : 260, flexShrink: 0,
            backgroundColor: colors.grey[800], borderRadius: "8px", p: 1.5,
            alignSelf: "flex-start", position: isMobile ? "static" : "sticky", top: 16,
          }}
        >
          <Typography variant="subtitle2" color={colors.grey[300]} sx={{ px: 1, pb: 0.5, letterSpacing: ".08em" }}>
            CONTENTS
          </Typography>
          <List dense>
            {index.chapters.map((c) => (
              <ListItemButton
                key={c.slug}
                selected={active?.slug === c.slug}
                onClick={() => navigate(`/help/${c.slug}`)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText primary={c.title} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Button
            fullWidth size="small" startIcon={<PictureAsPdfIcon />}
            component="a" href={`${HELP_BASE}/mkpace-manual.pdf`} target="_blank" rel="noopener"
            sx={{ textTransform: "none", color: colors.grey[300] }}
          >
            Download PDF
          </Button>
        </Box>

        {/* chapter */}
        <Box
          className="markdown-body"
          sx={{ flexGrow: 1, minWidth: 0, backgroundColor: colors.grey[800], borderRadius: "8px", p: isMobile ? 2 : 4 }}
        >
          {body ? (
            <HelpMarkdown
              chapterSlugs={index.chapters.map((c) => c.slug)}
              onChapterLink={(s) => navigate(`/help/${s}`)}
            >
              {body}
            </HelpMarkdown>
          ) : (
            <Typography color={colors.grey[300]}>Loading…</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Help;
